import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import anime from "animejs";
import { useNotificationStore } from "@/stores/notifications";
import { Bell, Check } from "lucide-react";
import { notificationDestination } from "@/lib/notificationNavigation";

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface DropdownCoords {
  top: number;
  left: number;
  width: number;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // The dropdown is portalled to <body> and positioned from the bell's rect.
  // The bell renders in TWO places (desktop sidebar + mobile drawer); only the
  // VISIBLE instance computes coords, so exactly one dropdown ever shows — and
  // portalling escapes the mobile drawer's `transform`, which would otherwise
  // trap a `fixed`/absolute panel off-screen (the PWA cut-off bug).
  const [coords, setCoords] = useState<DropdownCoords | null>(null);
  const {
    unreadCount,
    notifications,
    isOpen,
    isLoading,
    fetchUnreadCount,
    toggle,
    close,
    markRead,
    markAllRead,
  } = useNotificationStore();

  // Poll unread count every 30s (skip if this instance is hidden, e.g. mobile sidebar on desktop)
  useEffect(() => {
    if (panelRef.current && panelRef.current.offsetParent === null) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Position the portalled dropdown under the bell, clamped inside the viewport
  // so it can never run off-screen regardless of where the bell sits.
  useLayoutEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }
    const btn = buttonRef.current;
    // Hidden instance (the other bell) — let the visible one own the dropdown.
    if (!btn || btn.offsetParent === null) return;
    const compute = () => {
      const rect = btn.getBoundingClientRect();
      const margin = 8;
      const width = Math.min(320, window.innerWidth - margin * 2);
      // Anchor the dropdown's left edge to the bell (matches the old desktop
      // look), then clamp so it can never spill past either viewport edge.
      const left = Math.min(
        Math.max(margin, rect.left),
        window.innerWidth - width - margin,
      );
      const top = rect.bottom + margin;
      setCoords({ top, left, width });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [isOpen]);

  // Animate dropdown entrance
  useEffect(() => {
    if (!isOpen || !coords) return;
    const dropdown = dropdownRef.current;
    if (dropdown) {
      dropdown.style.willChange = "transform, opacity";
      anime({
        targets: dropdown,
        opacity: [0, 1],
        translateY: [-8, 0],
        scale: [0.97, 1],
        easing: "easeOutCubic",
        duration: 250,
        complete: () => {
          dropdown.style.willChange = "";
          dropdown.style.transform = "";
          dropdown.style.opacity = "";
        },
      });
    }
    // Stagger notification items
    const list = listRef.current;
    if (list && list.children.length > 0) {
      anime.set(list.children, { opacity: 0, translateX: -8 });
      anime({
        targets: list.children,
        opacity: [0, 1],
        translateX: [-8, 0],
        easing: "easeOutCubic",
        duration: 250,
        delay: anime.stagger(30, { start: 120 }),
        complete: () => {
          for (const child of Array.from(list.children)) {
            const el = child as HTMLElement;
            el.style.opacity = "";
            el.style.transform = "";
          }
        },
      });
    }
  }, [isOpen, coords]);

  // Close on outside click — the dropdown is portalled outside panelRef, so it
  // must be checked explicitly or clicking inside it would close the panel.
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (dropdownRef.current && dropdownRef.current.contains(target)) return;
      if (
        panelRef.current &&
        panelRef.current.offsetParent !== null &&
        !panelRef.current.contains(target)
      ) {
        close();
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClick);
      return () => document.removeEventListener("mousedown", handleClick);
    }
  }, [isOpen, close]);

  const handleNotificationClick = async (notification: (typeof notifications)[0]) => {
    if (!notification.is_read) {
      await markRead([notification.id]);
    }
    const destination = notificationDestination(notification.data);
    if (destination) navigate(destination);
    close();
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        ref={buttonRef}
        onClick={toggle}
        className="relative rounded-md p-1.5 text-ink-3 transition-colors hover:bg-panel-2 hover:text-ink-2"
        title="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-medium text-[#042027]">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen &&
        coords &&
        createPortal(
          <div
            ref={dropdownRef}
            className="card fixed z-[120] shadow-xl"
            style={{
              top: coords.top,
              left: coords.left,
              width: coords.width,
              opacity: 0,
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-line-2 px-4 py-3">
              <h3 className="text-sm font-medium text-ink">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="flex items-center gap-1 text-xs text-ink-3 transition-colors hover:text-ink-2"
                >
                  <Check className="h-3 w-3" />
                  Mark all read
                </button>
              )}
            </div>

            {/* List */}
            <div ref={listRef} className="max-h-[min(24rem,60vh)] overflow-y-auto">
              {isLoading && notifications.length === 0 ? (
                <p className="px-4 py-6 text-center text-xs text-ink-4">Loading...</p>
              ) : notifications.length === 0 ? (
                <p className="px-4 py-6 text-center text-xs text-ink-4">No notifications yet</p>
              ) : (
                notifications.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-panel-2 ${
                      !n.is_read ? "bg-panel-2/50" : ""
                    }`}
                  >
                    {!n.is_read && (
                      <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-accent" />
                    )}
                    <div className={`flex-1 min-w-0 ${n.is_read ? "ml-5" : ""}`}>
                      <p className="truncate text-sm font-medium text-ink">{n.title}</p>
                      <p className="mt-0.5 text-xs text-ink-3 line-clamp-2">{n.body}</p>
                      <p className="mt-1 text-[10px] text-ink-4">{timeAgo(n.created_at)}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
